import './global.css'
import './ui/theme/tokens.css'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Outlet, Route, Routes } from 'react-router-dom'
import ChatScreen from './screens/ChatScreen'
import BaseLayout from './components/layout/BaseLayout'
import StripeProvider from './components/payment/StripeProvider'
import FinishLineVideo from './components/progress/FinishLineVideo'
import AnnouncementOverlayContainer from './components/announcements/AnnouncementOverlayContainer'
import AuthRequiredView from './components/util/AuthRequiredView'
import ChallengeRoute from './components/util/ChallengeRoute'
import TeamRoute from './components/util/TeamRoute'
import ErrorBoundary from './components/util/ErrorBoundary'
import EventRequiredView from './components/util/EventRequiredView'
import ForceUpdateView from './components/util/ForceUpdateView'
import QueryInvalidator from './components/util/QueryInvalidator'
import RestDayRequiredView from './components/util/RestDayRequiredView'
import SafeAreaView from './components/util/SafeAreaView'
import TeamRequiredView from './components/util/TeamRequiredView'
import { ModalProvider } from './contexts/ModalContext'
import { usePaymentReturnListener } from './hooks/payment/usePaymentReturnListener'
import { usePushNotifications } from './hooks/util/usePushNotifications'
import { ScreenPath } from './navigation'
import ChallengeJoinScreen from './screens/ChallengeJoinScreen'
import CreateTeamScreen from './screens/CreateTeamScreen'
import ForgotPasswordScreen from './screens/ForgotPasswordScreen'
import FreeAgentScreen from './screens/FreeAgentScreen'
import HomeScreen from './screens/HomeScreen'
import JoinTeamScreen from './screens/JoinTeamScreen'
import LeaderboardScreen from './screens/LeaderboardScreen'
import LogScreen from './screens/LogScreen'
import GateScreenRoute from './screens/GateScreenRoute'
import LoginScreen from './screens/LoginScreen'
import MapScreen from './screens/MapScreen'
import PaymentIntroScreen from './screens/PaymentIntroScreen'
import PaymentSuccessScreen from './screens/PaymentSuccessScreen'
import ProfileScreen from './screens/ProfileScreen'
import ResetPasswordScreen from './screens/ResetPasswordScreen'
import SignUpScreen from './screens/SignUpScreen'
import TeamEntryRedirect from './screens/TeamEntryRedirect'
import TeamIntroScreen from './screens/TeamIntroScreen'

// Deploy

const queryClient = new QueryClient()

function PushNotificationsSetup() {
  usePushNotifications()
  return null
}

function PaymentReturnListenerSetup() {
  usePaymentReturnListener()
  return null
}

export default function App() {
  return (
    <SafeAreaView>
      <ErrorBoundary>
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            <PushNotificationsSetup />
            <PaymentReturnListenerSetup />
            <QueryInvalidator />
            <StripeProvider>
              <ModalProvider>
                <ForceUpdateView>
                  <FinishLineVideo>
                      <Routes>
                          <Route path='/' element={<BaseLayout />}>
                            {/* Auth */}
                            <Route path={ScreenPath.login} element={<LoginScreen />} />
                            <Route path={ScreenPath.signup} element={<SignUpScreen />} />
                            <Route
                              path={ScreenPath.forgotPassword}
                              element={<ForgotPasswordScreen />}
                            />
                            <Route
                              path={ScreenPath.resetPassword}
                              element={<ResetPasswordScreen />}
                            />
                            <Route
                              path='/'
                              element={
                                <AuthRequiredView>
                                  <Outlet />
                                </AuthRequiredView>
                              }
                            >
                              <Route path={ScreenPath.profile} element={<ProfileScreen />} />
                              <Route
                                path={`${ScreenPath.profile}/:userID`}
                                element={<ProfileScreen />}
                              />
                              <Route
                                path={ScreenPath.challenges}
                                element={<ChallengeJoinScreen />}
                              />
                              <Route
                                path={`${ScreenPath.challenges}/:challengeId`}
                                element={<ChallengeRoute />}
                              >
                                <Route path="team" element={<TeamIntroScreen />} />
                                <Route path="team/create" element={<CreateTeamScreen />} />
                                <Route path="team/join" element={<JoinTeamScreen />} />
                                <Route path="team/free-agent" element={<FreeAgentScreen />} />
                                <Route path="payment/intro" element={<PaymentIntroScreen />} />
                                <Route path="payment/success" element={<PaymentSuccessScreen />} />
                              </Route>
                              <Route path={ScreenPath.team} element={<TeamEntryRedirect />} />
                              <Route path={`${ScreenPath.team}/:teamID`} element={<TeamRoute />} />
                              <Route
                                path='/'
                                element={
                                  <TeamRequiredView>
                                    <AnnouncementOverlayContainer>
                                      <Outlet />
                                    </AnnouncementOverlayContainer>
                                  </TeamRequiredView>
                                }
                              >
                                {/* Chat */}
                                <Route path={'chat'} element={<ChatScreen />} />
                                <Route
                                  path='/'
                                  element={
                                    <EventRequiredView>
                                      <Outlet />
                                    </EventRequiredView>
                                  }
                                >
                                  {/* Home */}
                                  <Route path={ScreenPath.home} element={<HomeScreen />} />
                                  {/* Map */}
                                  <Route path={ScreenPath.map} element={<MapScreen />} />
                                  <Route
                                    path={`${ScreenPath.gate}/:gateId`}
                                    element={<GateScreenRoute />}
                                  />
                                  {/* Log */}
                                  <Route
                                    path={ScreenPath.log}
                                    element={
                                      <RestDayRequiredView>
                                        <LogScreen />
                                      </RestDayRequiredView>
                                    }
                                  />
                                  <Route
                                    path={`${ScreenPath.log}/:activityID`}
                                    element={
                                      <RestDayRequiredView>
                                        <LogScreen />
                                      </RestDayRequiredView>
                                    }
                                  />
                                  {/* Leaderboard */}
                                  <Route
                                    path={ScreenPath.leaderboard}
                                    element={<LeaderboardScreen />}
                                  />
                                </Route>
                              </Route>
                            </Route>
                          </Route>
                      </Routes>
                  </FinishLineVideo>
                  {/* <ReactQueryDevtools /> */}
                </ForceUpdateView>
              </ModalProvider>
            </StripeProvider>
          </QueryClientProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </SafeAreaView>
  )
}
